// Domain · CompanyChip
//
// Accepts a `CompanyJoin` (the FK-relation shape Supabase returns) and
// renders the canonical company affordance — an EntityLink in chip or
// inline variant. Owns the null + missing-label fallback so callers stop
// writing `r.company?.id ?? r.company_id ?? 0` ladders in their
// `data` mappers.
//
// Variants mirror `EntityChip` / `EntityLink`:
//   - `"chip"`   — full chip chrome (icon · label · arrow).
//   - `"inline"` (default) — link-styled text with a trailing arrow.

import React from "react";
import { withExportSpec } from "@ds/display/exportSpec";
import { absoluteAppUrl, getEntityRoute } from "@ds/navigation/navigation";
import { Space } from "antd";
import type { CompanyJoin } from "@shared/types/company";
import { useEntityLinkRenderer } from "@ds/display/entityLinkRendererContext";
import { TextValue } from "@ds/display/TextValue";
import { Pill } from "@ds/display/Pill";
import { SPACE } from "@ds/tokens/palette";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface CompanyChipInput {
  /**
   * Joined company row from Supabase (`r.company`, `r.load_place_company`, …).
   *
   * Three states:
   *   - real company (positive `id`)         → linked chip (arrow + click)
   *   - raw company (no/zero `id` but a
   *     `short_name`) → plain-text label, no link. Some EDPR / EDPR-request
   *     parties are scraped from BDO and stored as TEXT without a matching
   *     `companies` row; they're displayed but not navigable.
   *   - null / undefined / no name           → em-dash
   */
  company: CompanyJoin | null | undefined;
  /** Rendering variant. Default `"inline"` for table density. */
  variant?: "chip" | "inline";
  /** Size scale. Default `md`. Only applies when `variant="chip"`. */
  size?: "sm" | "md";
  /**
   * When set, renders a trailing amber "Do weryfikacji" pill next to the
   * link. Matches the list-page convention for `is_pending_review`. The
   * chip is suppressed when `company` is null.
   */
  pendingReview?: boolean;
  /** `data-testid` on the root. Defaults to `"company-chip"`. */
  testId?: string;
}

export type CompanyChipProps = CompanyChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function CompanyChip({
  company,
  variant = "inline",
  size,
  pendingReview = false,
  testId = "company-chip",
}: CompanyChipProps) {
  const renderLink = useEntityLinkRenderer();
  // Raw company (no real `companies` row) — display the saved name as plain
  // text, no link affordance. Only fall through to the em-dash when there's
  // genuinely nothing to show.
  if (!company?.id) {
    if (company?.short_name) {
      const pendingNode = pendingReview ? (
        <Pill tone="amber" size="sm">Do weryfikacji</Pill>
      ) : null;
      const labelNode = (
        <TextValue
          as="span"
          tone="primary"
          size={variant === "chip" ? "md" : "sm"}
          testId={testId}
        >
          {company.short_name}
        </TextValue>
      );
      return pendingNode
        ? <Space size={SPACE.xs}>{labelNode}{pendingNode}</Space>
        : labelNode;
    }
    return <span data-testid={testId}>—</span>;
  }
  const link = renderLink({
    type: "company",
    id: company.id,
    label: company.short_name ?? `#${company.id}`,
    variant: variant === "chip" ? "pill" : "inline",
    size,
    testId,
  }) as React.ReactElement;
  if (!pendingReview) return link;
  return (
    <Space size={SPACE.xs}>
      {link}
      <Pill tone="amber" size="sm">
        Do weryfikacji
      </Pill>
    </Space>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// The chip renders an EntityLink, but `deriveExportAs` reads the DECLARED
// component, not what it renders — so the tag has to live here too. Without it
// every company cell exported as inert text while the hand-declared transaction
// reference column exported a working link.
//
// A company with a `short_name` but no `id` is a BDO-scraped party: shown, not
// navigable. It exports as its name with no hyperlink, matching the render.
//
// `pendingReview` rides along in the label. The companies list paints it as a
// trailing amber pill, and that text used to reach the workbook through the
// shadow-DOM path; exporting the bare link label would drop it. Its own
// `is_pending_review` column is `defaultVisible: false`, so nothing else in a
// default export carries it.

withExportSpec<typeof CompanyChip, CompanyChipProps>(CompanyChip, {
  type: "link",
  value: (p) => {
    const id = p.company?.id;
    // `||` not `??`: an empty `short_name` is no name, and a marker with
    // nothing in front of it is worse than no cell at all.
    const label = p.company?.short_name || (id ? `#${id}` : null);
    if (label == null) return undefined;
    return p.pendingReview ? `${label} · Do weryfikacji` : label;
  },
  hyperlink: (p) =>
    p.company?.id ? absoluteAppUrl(getEntityRoute("company", p.company.id)) : undefined,
});
